import { Loader2 } from "lucide-react";
import { fmtScheduled } from "./BroadcastScheduleField";

/**
 * Renders one of three status banners based on the result payload:
 *   • Sent (immediate, background finished)
 *   • Queued / Sending (immediate, background in flight — auto-polling in parent)
 *   • Scheduled (future dispatch)
 */
export default function BroadcastResultBanner({ result }) {
  if (!result) return null;
  const inFlight = result.queued || result.status === "queued" || result.status === "sending";

  if (result.ab) {
    return (
      <div
        className="border-l-2 border-rose-500 pl-3 py-2 text-sm text-rose-300 space-y-1"
        data-testid="broadcast-result-ab"
      >
        <div>
          ⚡ A/B campaign launched — variants A + B are going out to{" "}
          <b>{result.test_percent}%</b> of the audience each.
        </div>
        <div className="text-xs text-zinc-400">
          Winner will blast to the remaining reserved audience in{" "}
          <b>{result.test_duration_minutes} min</b>. Campaign ID:{" "}
          <code className="text-white">{result.campaign_id}</code>
        </div>
      </div>
    );
  }

  if (result.scheduled) {
    return (
      <div className="border-l-2 border-amber-500 pl-3 py-2 text-sm text-amber-400">
        Scheduled for {fmtScheduled(result.scheduled_for)}.
      </div>
    );
  }
  if (inFlight) {
    return (
      <div
        className="border-l-2 border-amber-500 pl-3 py-2 text-sm text-amber-400 flex items-center gap-2"
        data-testid="broadcast-result-queued"
      >
        <Loader2 size={14} className="animate-spin" />
        {result.status === "sending"
          ? `Sending to ${result.recipients_total} members…`
          : "Queued — background job is picking it up."}
      </div>
    );
  }
  // "Sent" state — a HISTORICAL campaign result, labeled with its send time.
  // Never asserts anything about current email configuration.
  const total = result.recipients_total || 0;
  const notified = result.recipients_notified || 0;
  const counts = result.counts || {
    accepted: result.recipients_dispatched || 0,
    delivered: 0, bounced: 0,
    failed: Math.max(0, total - (result.recipients_dispatched || 0)),
  };
  const sentAt = result.sent_at || result.created_at;
  const lowAcceptance = total > 5 && counts.accepted <= 1;
  return (
    <div
      className="border-l-2 border-emerald-500 pl-3 py-2 text-sm space-y-1"
      data-testid="broadcast-result-sent"
    >
      <div className="text-[10px] uppercase tracking-widest text-zinc-500" data-testid="broadcast-result-timestamp">
        Campaign result · sent {sentAt ? new Date(sentAt).toLocaleString() : "—"}
      </div>
      <div className="text-emerald-400">
        📬 <b>{notified}/{total}</b> in-app bells written.
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1" data-testid="broadcast-result-counts">
        <CountChip label="Accepted" value={counts.accepted} tone="text-emerald-400" />
        <CountChip label="Delivered" value={counts.delivered} tone="text-sky-400" />
        <CountChip label="Bounced" value={counts.bounced} tone="text-amber-400" />
        <CountChip label="Failed" value={counts.failed} tone="text-rose-400" />
      </div>
      <div className="text-[10px] text-zinc-500 pt-0.5">
        Delivered/bounced update as Resend webhooks arrive.
      </div>
      {lowAcceptance && (
        <div className="mt-2 border-l-2 border-rose-500 pl-3 py-1.5 text-xs text-rose-300 leading-relaxed" data-testid="broadcast-result-low-acceptance">
          Most emails were not accepted at send time for this past campaign.
          Common causes: unverified Resend domain or quota limits <i>at the time it was sent</i>.
          The currently configured sender is shown below the composer — this
          historical result does not reflect current configuration.
        </div>
      )}
    </div>
  );
}

function CountChip({ label, value, tone }) {
  return (
    <div className="bg-black/30 border border-white/10 px-2 py-1.5">
      <div className={`text-sm font-semibold ${tone}`}>{value ?? 0}</div>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
    </div>
  );
}
